import { useEffect } from 'react'
import {
  KEYBOARD_INSET_VAR,
  computeKeyboardInset,
  isObscured,
  isTextEntry,
} from '@/lib/keyboardInset'

/**
 * Makes the app aware of the software keyboard. Mounted ONCE, app-wide.
 *
 * Two jobs, and they are separate because they fix different complaints:
 *
 *  1. Publish the keyboard's height as `--keyboard-inset` on the root element,
 *     so CSS can give the keyboard its space instead of drawing underneath it.
 *     `index.css` shortens `#root` by it, which shrinks every screen's internal
 *     scroll container; `dialog.tsx` and the assistant panel read it directly
 *     because they are `fixed` and so are positioned against the viewport, not
 *     against `#root`.
 *
 *  2. Scroll the field somebody is typing into back into view when the keyboard
 *     has covered it — the direct answer to "I can't read what's on screen".
 *
 * All of it is inert without a software keyboard: `visualViewport` is absent on
 * old browsers, the inset stays 0 on a desktop, and nothing scrolls unless a
 * field is actually obscured. There are no width checks anywhere on purpose —
 * "phone" is not a width, it is the presence of a keyboard eating the viewport.
 */
export function useKeyboardAware(): void {
  useEffect(() => {
    const root = document.documentElement
    const vv = window.visualViewport
    if (!vv) return

    // The element to keep visible. Held across events because the keyboard
    // finishes animating well after focusin fires, so the useful measurement is
    // the one taken on the resize it causes — not the one taken on focus, when
    // nothing has moved yet.
    let watching: HTMLElement | null = null
    let settleTimer: number | undefined

    const publish = () => {
      const inset = computeKeyboardInset(window.innerHeight, vv.height, vv.offsetTop)
      // Written only on change: this fires on every visual-viewport scroll frame
      // on iOS, and setting a custom property invalidates style for the subtree.
      const next = `${inset}px`
      if (root.style.getPropertyValue(KEYBOARD_INSET_VAR) !== next) {
        root.style.setProperty(KEYBOARD_INSET_VAR, next)
      }
      return inset
    }

    const revealWatched = () => {
      const el = watching
      if (!el || !el.isConnected) return
      // The visible bottom edge in LAYOUT-viewport coordinates, which is the
      // space getBoundingClientRect reports in. On iOS the visual viewport is
      // pushed up rather than shrunk, so offsetTop has to be added back.
      const visibleBottom = vv.offsetTop + vv.height
      if (!isObscured(el.getBoundingClientRect().bottom, visibleBottom)) return
      el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }

    const onViewportChange = () => {
      const inset = publish()
      // Only chase the field while a keyboard is actually up; on the way back
      // down, leave the scroll position where the person left it.
      if (inset > 0) revealWatched()
      else watching = null
    }

    const onFocusIn = (e: FocusEvent) => {
      const el = e.target as HTMLElement | null
      if (!el || typeof el.tagName !== 'string') return
      const type = el instanceof HTMLInputElement ? el.type : null
      if (!isTextEntry(el.tagName, type, el.isContentEditable)) {
        watching = null
        return
      }
      watching = el
      // Fallback for the case where focusing raises no resize event — an already
      // open keyboard being handed a second field. 350ms clears a keyboard
      // animation on both platforms.
      window.clearTimeout(settleTimer)
      settleTimer = window.setTimeout(revealWatched, 350)
    }

    const onFocusOut = () => { watching = null }

    publish()
    vv.addEventListener('resize', onViewportChange)
    vv.addEventListener('scroll', onViewportChange)
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)

    return () => {
      window.clearTimeout(settleTimer)
      vv.removeEventListener('resize', onViewportChange)
      vv.removeEventListener('scroll', onViewportChange)
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
      // Leaving a stale inset behind would shorten the app with no keyboard up.
      root.style.removeProperty(KEYBOARD_INSET_VAR)
    }
  }, [])
}
